import { normalizeUnusedInvoiceLine } from "../../shared/invoice-line";
import { DocumentTaxError, parseDocumentTaxes, parseTaxSet } from "../../shared/document-taxes";
import { validateV1Taxes } from "./taxes";
import { parseIdentifier } from "../clients/input";
import { InvoiceInputError, type DiscountInput, type InvoiceDraftInput, type InvoiceLineInput } from "./types";
import { INVOICE_TERM_DAYS, addInvoiceDays, termForInvoiceDueDate, type InvoiceTerm } from "../../shared/invoice-terms";
import { lineSearchText, parseRichDetails } from "../../shared/document-line-content";

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new InvoiceInputError("Invalid input");
  return value as Record<string, unknown>;
}
function exact(value: Record<string, unknown>, keys: readonly string[]) {
  if (Object.keys(value).some((key) => !keys.includes(key))) throw new InvoiceInputError("Invalid input");
}
function optionalId(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string") throw new InvoiceInputError("Invalid input");
  try { return parseIdentifier(value); } catch { throw new InvoiceInputError("Invalid input"); }
}
function optionalText(value: unknown, maximum: number): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string" || value.trim().length > maximum) throw new InvoiceInputError("Invalid input");
  return value.trim() || null;
}
function date(value: unknown): string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new InvoiceInputError("Invalid date");
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== value) throw new InvoiceInputError("Invalid date");
  return value;
}
function quantity(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string" || !/^\d{1,5}(?:\.\d{1,3})?$/.test(value)) throw new InvoiceInputError("Invalid quantity");
  const [whole, fraction = ""] = value.split(".");
  const result = Number(whole) * 1000 + Number(fraction.padEnd(3, "0"));
  if (result < 1 || result > 99_999_999) throw new InvoiceInputError("Invalid quantity");
  return result;
}
function money(value: unknown, signed = false): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string" || !(signed ? /^-?\d{1,8}(?:\.\d{1,2})?$/ : /^\d{1,8}(?:\.\d{1,2})?$/).test(value)) throw new InvoiceInputError("Invalid amount");
  const negative = value.startsWith("-");
  const [whole, fraction = ""] = (negative ? value.slice(1) : value).split(".");
  const magnitude = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  const result = negative ? -magnitude : magnitude;
  if (!Number.isSafeInteger(result) || Math.abs(result) > 9_999_999_999) throw new InvoiceInputError("Invalid amount");
  return result;
}
function basisPoints(value: unknown): number {
  if (typeof value !== "string" || !/^\d{1,3}(?:\.\d{1,2})?$/.test(value)) throw new InvoiceInputError("Invalid percentage");
  const [whole, fraction = ""] = value.split(".");
  const result = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (result < 0 || result > 10_000) throw new InvoiceInputError("Invalid percentage");
  return result;
}
function line(value: unknown, v1 = false): InvoiceLineInput {
  const input = object(value); exact(input, ["description", "title", "details", "taxMode", "quantity", "rate", "taxRateId", "taxSet"]);
  if (typeof input.description !== "string" || input.description.includes("\0")) throw new InvoiceInputError("Invalid description");
  if (input.title !== undefined && (typeof input.title !== "string" || input.title.length > 500 || input.title.includes("\0"))) throw new InvoiceInputError("Invalid line title");
  let details;
  try { details = input.details === undefined ? undefined : parseRichDetails(input.details); } catch { throw new InvoiceInputError("Invalid line details"); }
  if (input.title === undefined && input.details !== undefined) throw new InvoiceInputError("Line title is required with details");
  const description = input.title === undefined ? input.description : lineSearchText(input.title, details ?? []);
  if (input.taxMode !== undefined && !["inherit", "none", "override"].includes(String(input.taxMode))) throw new InvoiceInputError("Invalid line tax mode");
  const parsedQuantity = quantity(input.quantity);
  const parsedRate = money(input.rate, true);
  if ((parsedQuantity === null) !== (parsedRate === null)) throw new InvoiceInputError("Quantity and rate must both be present");
  const taxRateId = optionalId(input.taxRateId);
  const taxMode = input.taxMode as InvoiceLineInput["taxMode"] ?? (taxRateId ? "override" : "none");
  if (!v1 && (taxMode === "override" && !taxRateId || taxMode !== "override" && taxRateId)) throw new InvoiceInputError("Invalid line tax selection");
  let taxSet;
  if (v1) {
    if (input.taxMode === undefined) throw new InvoiceInputError("v1 lines require a tax mode");
    try { taxSet = parseTaxSet(input.taxSet ?? []); } catch (error) { throw new InvoiceInputError(error instanceof Error ? error.message : "Invalid tax set"); }
  } else if (input.taxSet !== undefined) throw new InvoiceInputError("Tax sets require tax model version 1");
  return normalizeUnusedInvoiceLine({ description, ...(input.title === undefined ? {} : { title: input.title.trim(), details: details ?? [] }), quantityThousandths: parsedQuantity, unitAmountMinor: parsedRate, taxRateId: v1 ? null : taxRateId, ...(v1 ? {taxSet} : {}), ...(input.taxMode === undefined ? {} : {taxMode}) });
}
function discount(value: unknown): DiscountInput | null {
  if (value === null || value === undefined) return null;
  const input = object(value); exact(input, ["kind", "value", "label"]);
  const label = optionalText(input.label, 120);
  if (input.kind === "percentage") return { kind: "percentage", basisPoints: basisPoints(input.value), label };
  if (input.kind === "fixed_amount") {
    const amountMinor = money(input.value);
    if (amountMinor === null) throw new InvoiceInputError("Invalid discount");
    return { kind: "fixed_amount", amountMinor, label };
  }
  throw new InvoiceInputError("Invalid discount");
}

export function parseInvoiceDraftInput(value: unknown): InvoiceDraftInput {
  const input = object(value); exact(input, ["clientId", "recipientContactId", "issueDate", "dueDate", "termLabel", "note", "reference", "paymentInstructions", "discount", "lines", "number", "numberManuallyEdited", "defaultTaxRateId", "taxModelVersion", "taxes"]);
  if (!Array.isArray(input.lines) || input.lines.length > 100) throw new InvoiceInputError("Invalid lines");
  const clientId = optionalId(input.clientId);
  const recipientContactId = optionalId(input.recipientContactId);
  if (!clientId && recipientContactId) throw new InvoiceInputError("Recipient requires a client");
  if (input.number !== undefined && (typeof input.number !== "string" || !input.number.trim() || input.number.trim().length > 120 || input.number.includes("\0"))) throw new InvoiceInputError("Invalid invoice number");
  if (input.numberManuallyEdited !== undefined && typeof input.numberManuallyEdited !== "boolean") throw new InvoiceInputError("Invalid invoice number state");
  const issueDate = date(input.issueDate);
  const dueDate = input.dueDate === undefined ? undefined : date(input.dueDate);
  if (input.termLabel !== undefined && (typeof input.termLabel !== "string" || !(input.termLabel === "Custom" || Object.hasOwn(INVOICE_TERM_DAYS, input.termLabel)))) throw new InvoiceInputError("Invalid terms");
  const termLabel = input.termLabel as InvoiceTerm | undefined ?? (dueDate ? termForInvoiceDueDate(issueDate, dueDate) : "Net 30");
  if (termLabel === "Custom" && !dueDate) throw new InvoiceInputError("Custom terms require a due date");
  const resolvedDueDate = dueDate ?? addInvoiceDays(issueDate, INVOICE_TERM_DAYS[termLabel as keyof typeof INVOICE_TERM_DAYS]);
  if (termLabel !== "Custom" && resolvedDueDate !== addInvoiceDays(issueDate, INVOICE_TERM_DAYS[termLabel])) throw new InvoiceInputError("Due date does not match terms");
  if (input.taxModelVersion !== undefined && input.taxModelVersion !== null && input.taxModelVersion !== 1) throw new InvoiceInputError("Unsupported tax model version");
  const v1 = input.taxModelVersion === 1;
  let taxes;
  if (v1) {
    try { taxes = parseDocumentTaxes(input.taxes); } catch (error) { if (error instanceof DocumentTaxError) throw new InvoiceInputError(error.message); throw error; }
  } else if (input.taxes !== undefined) throw new InvoiceInputError("Tax sets require tax model version 1");
  const parsed: InvoiceDraftInput = { clientId, recipientContactId, issueDate, dueDate: resolvedDueDate, termLabel, note: optionalText(input.note, 2000), reference: optionalText(input.reference, 200), paymentInstructions: optionalText(input.paymentInstructions, 2000), discount: discount(input.discount), lines: input.lines.map(value => line(value, v1)), ...(v1 ? {taxModelVersion: 1, taxes, defaultTaxRateId:null} : input.defaultTaxRateId === undefined ? {} : {defaultTaxRateId:optionalId(input.defaultTaxRateId)}), ...(input.number === undefined ? {} : {number: input.number.trim()}), ...(input.numberManuallyEdited === undefined ? {} : {numberManuallyEdited:input.numberManuallyEdited}) };
  validateV1Taxes(parsed);
  return parsed;
}

export function parseInvoiceVersion(value: unknown): number {
  if (!Number.isInteger(value) || Number(value) < 1) throw new InvoiceInputError("Invalid version");
  return Number(value);
}

export function parseTaxRateInput(value: unknown): { label: string; rateBasisPoints: number; taxNumber?: string | null } {
  const input = object(value); exact(input, ["label", "rate", "taxNumber"]);
  const label = optionalText(input.label, 120);
  const rateBasisPoints = basisPoints(input.rate);
  if (!label || rateBasisPoints === 0) throw new InvoiceInputError("Invalid tax rate");
  return { label, rateBasisPoints, ...(input.taxNumber === undefined ? {} : {taxNumber:optionalText(input.taxNumber,120)}) };
}
